import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './Branding.css';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Badge } from "../../ui/badge";
import { Switch } from "../../ui/switch";
import { ScrollArea } from "../../ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import {
  Globe,
  Plus,
  CheckCircle2,
  Clock,
  XCircle,
  Shield,
  ShieldAlert,
  Pencil,
  Trash2,
  Copy,
  AlertCircle,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  DomainBrand,
  getDomainBrands,
  addDomainBrand,
  updateDomainBrand,
  deleteDomainBrand,
} from "../../utils/branding-api";

interface BrandingProps {
  embedded?: Boolean;
  onBack?: () => void;
}

function Branding( {embedded, onBack} : BrandingProps) {
  const { token, isAuthenticated } = useAuth();
  const [domainBrands, setDomainBrands] = useState<DomainBrand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Add/Edit modal state
  const [showModal, setShowModal] = useState(false);
  const [editingDomain, setEditingDomain] = useState<DomainBrand | null>(null);
  const [modalDomain, setModalDomain] = useState('');
  const [modalSSLEnabled, setModalSSLEnabled] = useState(false);
  const [modalSubmitting, setModalSubmitting] = useState(false);

  // Dialog state
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState<DomainBrand | null>(null);
  const [newDomainName, setNewDomainName] = useState('');
  const [newDomainSSL, setNewDomainSSL] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      fetchDomainBrands();
    }
  }, [isAuthenticated]);

  const fetchDomainBrands = async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = await getDomainBrands(token);
      setDomainBrands(data);
    } catch (err) {
      console.error('Error fetching domain brands:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDomain = async () => {
    if (!token) {
      toast.error('You must be logged in to add domains');
      return;
    }

    if (!newDomainName.trim()) {
      toast.error('Please enter a domain name');
      return;
    }

    try {
      await addDomainBrand(newDomainName, newDomainSSL, token);
      toast.success('Domain brand added successfully! DNS verification will begin shortly.');
      setIsAddDialogOpen(false);
      setNewDomainName('');
      setNewDomainSSL(true);
      await fetchDomainBrands();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add domain');
    }
  };

  const handleEditDomain = async () => {
    if (!token || !selectedDomain) {
      toast.error('Invalid request');
      return;
    }

    try {
      await updateDomainBrand(selectedDomain.id, selectedDomain.domain, selectedDomain.sslEnabled, token);
      toast.success('Domain brand updated successfully!');
      setIsEditDialogOpen(false);
      setSelectedDomain(null);
      await fetchDomainBrands();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update domain');
    }
  };

  const handleDeleteDomain = async () => {
    if (!token || !selectedDomain) {
      toast.error('Invalid request');
      return;
    }

    try {
      await deleteDomainBrand(selectedDomain.id, token);
      toast.success('Domain brand deleted successfully');
      setIsDeleteDialogOpen(false);
      setSelectedDomain(null);
      await fetchDomainBrands();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete domain');
    }
  };

  const handleCopyCNAME = (domain: string) => {
    navigator.clipboard.writeText(`${domain} CNAME confcall.app`);
    toast.success('CNAME record copied to clipboard!');
  };


  if (!isAuthenticated) {
    return (
      <div className="branding">
        <div className="auth-required">
          <p>Please sign in to manage your domain brands.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="branding">
        <div className="loading">Loading domain brands...</div>
      </div>
    );
  }

  const getVerificationBadge = (domain: DomainBrand) => {
    if (domain.verified) {
        return (
          <Badge variant="secondary" className="gap-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">
            <CheckCircle2 className="size-3" />
            Domain Verified
          </Badge>
        );
      }
      else { // "pending":
        return (
          <Badge variant="secondary" className="gap-1 bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100">
            <Clock className="size-3" />
            Domain Pending
          </Badge>
        );
      }
  };

  const getSSLBadge = (domain: DomainBrand) => {
    if (domain.sslEnabled) {
      if (domain.certificateProvisioned) { //"active":
        return (
          <Badge variant="secondary" className="gap-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">
            <Shield className="size-3" />
            Certificate Provisioned
          </Badge>
        );
      }
      if (domain.certificateError) { // "failed":
        return (
          <Badge variant="secondary" className="gap-1 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100">
            <ShieldAlert className="size-3" />
            Certificate Provisioned failed Failed
          </Badge>
        );
      }
      else { // "pending":
        return (
          <Badge variant="secondary" className="gap-1 bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100">
            <Clock className="size-3" />
            Certificate Pending
          </Badge>
        );
      }

    }
    else {
        return (
          <Badge variant="outline" className="gap-1">
           SSL Disabled
          </Badge>
        );
    }
  };

  return (
    <div className="h-screen w-screen flex flex-col bg-background">
    {/* Header */}
    <div className="border-b bg-background p-4">
      { embedded && (
        <Button variant="ghost" onClick={onBack} className="mb-2">
          ← Back to URLs
        </Button>)
      }
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Domain Management</h1>
          <p className="text-sm text-muted-foreground">
            Manage your custom domains for URL shortening
          </p>
        </div>
        <Button onClick={() => setIsAddDialogOpen(true)}>
          <Plus className="size-4" />
          Add Domain
        </Button>
      </div>
    </div>

    {/* Content */}
    <ScrollArea className="flex-1 p-6">
      <div className="space-y-4 max-w-6xl mx-auto">
        {domainBrands.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Globe className="size-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No domains configured</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Add your first custom domain to get started
              </p>
              <Button onClick={() => setIsAddDialogOpen(true)}>
                <Plus className="size-4" />
                Add Domain
              </Button>
            </CardContent>
          </Card>
        ) : (
          domainBrands.map((domain) => (
            <Card key={domain.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-xl flex items-center gap-2">
                      <Globe className="size-5" />
                      {domain.domain}
                    </CardTitle>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      {getVerificationBadge(domain)}
                      {getSSLBadge(domain)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedDomain(domain);
                        setIsEditDialogOpen(true);
                      }}
                    >
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedDomain(domain);
                        setIsDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Verification Instructions */}
                {!domain.verified && (
                  <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-900">
                    <div className="flex gap-2 mb-3">
                      <AlertCircle className="size-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                          Domain Verification Pending
                        </h4>
                        <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
                          After adding your domain:
                        </p>
                        <ol className="text-sm text-blue-800 dark:text-blue-200 space-y-2 list-decimal list-inside">
                          <li>Configure the CNAME record with your DNS provider</li>
                          <li>Wait for DNS propagation (up to 48 hours)</li>
                          <li>Domain will be automatically verified once DNS is configured</li>
                          <li>If SSL is enabled, certificate will be provisioned after verification</li>
                        </ol>
                        <div className="mt-3 p-3 bg-blue-100 dark:bg-blue-900/50 rounded border border-blue-300 dark:border-blue-800">
                          <div className="flex items-center justify-between">
                            <code className="text-xs font-mono">
                              {domain.domain} CNAME → proxy.shortener.app
                            </code>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleCopyCNAME(domain.domain)}
                              className="h-7"
                            >
                              <Copy className="size-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* SSL Error Message */}
                {domain.certificateError && (
                  <div className="p-3 rounded-lg border bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900">
                    <div className="flex gap-2">
                      <ShieldAlert className="size-4 flex-shrink-0 mt-0.5 text-red-600 dark:text-red-400" />
                      <p className="text-sm text-red-800 dark:text-red-200">
                        {domain.certificateError}
                      </p>
                    </div>
                  </div>
                )}

                {/* Timestamps */}
                <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Added</p>
                    <p className="text-sm font-medium">
                      {format(new Date(domain.uploadedAt), "MMM dd, yyyy 'at' HH:mm")}
                    </p>
                  </div>
                  {domain.updatedAt && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Last Updated</p>
                      <p className="text-sm font-medium">
                        {format(new Date(domain.updatedAt), "MMM dd, yyyy 'at' HH:mm")}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </ScrollArea>

    {/* Add Domain Dialog */}
    <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Domain</DialogTitle>
          <DialogDescription>
            Add a custom domain for your shortened URLs. You'll need to configure DNS settings after adding.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="domain-name">Domain Name</Label>
            <Input
              id="domain-name"
              placeholder="example.com"
              value={newDomainName}
              onChange={(e) => setNewDomainName(e.target.value)}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="ssl-enabled">Enable SSL</Label>
              <p className="text-xs text-muted-foreground">
                Automatically provision SSL certificate after verification
              </p>
            </div>
            <Switch id="ssl-enabled" checked={newDomainSSL} onCheckedChange={setNewDomainSSL} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleAddDomain} disabled={!newDomainName.trim()}>
            Add Domain
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Edit Domain Dialog */}
    <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Domain</DialogTitle>
          <DialogDescription>Update domain settings and configuration.</DialogDescription>
        </DialogHeader>
        {selectedDomain && (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Domain Name</Label>
              <Input value={selectedDomain.domain} disabled className="bg-muted" />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="edit-ssl-enabled">Enable SSL</Label>
                <p className="text-xs text-muted-foreground">
                  Enable or disable SSL certificate for this domain
                </p>
              </div>
              <Switch
                id="edit-ssl-enabled"
                checked={selectedDomain.sslEnabled}
                onCheckedChange={(checked) => {
                  setSelectedDomain({
                    ...selectedDomain,
                    sslEnabled: checked,
                  });
                }}
              />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleEditDomain}>Save Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Delete Confirmation Dialog */}
    <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently delete the domain <strong>{selectedDomain?.domain}</strong>. All shortened URLs
            using this domain will stop working. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleDeleteDomain} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
            Delete Domain
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
  );
}

export default Branding;
